import React, { useCallback, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import { Autocomplete, Box, Button, Checkbox, IconButton, Stack, TextField, Typography } from '@mui/material';
import { DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

import { brands as ALL_BRANDS, brandsByValue } from '~/consts/brands';

import { CreateCampaignPayload } from '~/services/AccommodationService';
import { Dayjs, formatDateISO, newDate } from '~/services/TimeService';

export type CampaignScheduleRow = NonNullable<CreateCampaignPayload['schedules']>[number];

interface Props {
  value: Array<CampaignScheduleRow>;
  onChange: (next: Array<CampaignScheduleRow>) => void;
}

let nextRowKey = 0;
const allocateRowKey = () => `row-${++nextRowKey}`;

const toDayjs = (value: string): Dayjs | null => (value ? newDate(value) : null);
const fromDayjs = (value: Dayjs | null): string => (value ? formatDateISO(value.toDate()) : '');

/**
 * Strips empty travel dates before submission — `travelStartDate`/`travelEndDate` are optional on
 * the contract (an omitted pair means no stay-date restriction), but the editor always keeps them
 * as controlled string fields, so an unset date is `''` locally rather than absent. Sending `''`
 * through would fail the backend's ISO8601 validation instead of being treated as "no restriction".
 */
export function sanitizeSchedulesForSubmit(schedules: Array<CampaignScheduleRow>): Array<CampaignScheduleRow> {
  return schedules.map(({ travelStartDate, travelEndDate, ...rest }) => ({
    ...rest,
    ...(travelStartDate ? { travelStartDate } : {}),
    ...(travelEndDate ? { travelEndDate } : {}),
  }));
}

// Defaults new rows to every brand selected — an empty `brands` array matches no brand at all
// (svc-accommodation filters schedules with `brands.includes(brand)`), so it's a silently dead
// schedule rather than a wildcard. Defaulting to "all" keeps that footgun out of the common path.
const newRow = (): CampaignScheduleRow => ({
  brands: ALL_BRANDS.map((brand) => brand.value),
  activeStartDate: '',
  activeEndDate: '',
  travelStartDate: '',
  travelEndDate: '',
});

/**
 * Editor for a campaign's `schedules` — the periods (and optional stay-date restrictions) when a
 * campaign can be merchandised. Mirrors AccommodationBundleSchedulesTab's row editor since bundles
 * and campaigns share the same schedule shape on the contract.
 */
function CampaignScheduleEditor({ value, onChange }: Props) {
  const [rowKeys, setRowKeys] = useState<Array<string>>(() => value.map(allocateRowKey));

  const updateRow = useCallback(
    (index: number, key: keyof CampaignScheduleRow, rowValue: string | Array<string>) => {
      onChange(value.map((row, i) => (i === index ? { ...row, [key]: rowValue } : row)));
    },
    [value, onChange],
  );

  const updateRowRange = useCallback(
    (
      index: number,
      startKey: keyof CampaignScheduleRow,
      endKey: keyof CampaignScheduleRow,
      [start, end]: [Dayjs | null, Dayjs | null],
    ) => {
      onChange(
        value.map((row, i) => (i === index ? { ...row, [startKey]: fromDayjs(start), [endKey]: fromDayjs(end) } : row)),
      );
    },
    [value, onChange],
  );

  const addRow = useCallback(() => {
    onChange([...value, newRow()]);
    setRowKeys((prev) => [...prev, allocateRowKey()]);
  }, [value, onChange]);

  const removeRow = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
      setRowKeys((prev) => prev.filter((_, i) => i !== index));
    },
    [value, onChange],
  );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Schedules restrict when the campaign is merchandised and which travel dates it covers. An empty list means the
        campaign is always on (subject to its status).
      </Typography>

      {value.map((row, index) => (
        <Box key={rowKeys[index]} border={1} borderColor="divider" borderRadius={1} p={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2">Schedule #{index + 1}</Typography>
            <IconButton onClick={() => removeRow(index)} size="small" aria-label="Remove schedule">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
          <Stack spacing={2}>
            <Autocomplete
              multiple
              fullWidth
              openOnFocus
              disableCloseOnSelect
              options={ALL_BRANDS.map((brand) => brand.value)}
              value={row.brands}
              onChange={(_e, next) => updateRow(index, 'brands', next)}
              getOptionLabel={(value) => brandsByValue[value]?.title ?? value}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} />
                  {brandsByValue[option]?.title ?? option}
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Brands" placeholder="Select brands" />}
            />
            <Box display="flex" gap={2}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box flex={1} border={1} borderColor="divider" borderRadius={1} p={1.5}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Active period — when the campaign can be merchandised
                  </Typography>
                  <DateRangePicker
                    localeText={{ start: 'Active Start Date', end: 'Active End Date' }}
                    format="DD/MM/YYYY"
                    value={[toDayjs(row.activeStartDate), toDayjs(row.activeEndDate)]}
                    onChange={(newValue) => updateRowRange(index, 'activeStartDate', 'activeEndDate', newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Box>

                <Box flex={1} border={1} borderColor="divider" borderRadius={1} p={1.5}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Travel dates — optional restriction on stay dates
                  </Typography>
                  <DateRangePicker
                    localeText={{ start: 'Travel Start Date', end: 'Travel End Date' }}
                    format="DD/MM/YYYY"
                    value={[toDayjs(row.travelStartDate), toDayjs(row.travelEndDate)]}
                    onChange={(newValue) => updateRowRange(index, 'travelStartDate', 'travelEndDate', newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Box>
              </LocalizationProvider>
            </Box>
          </Stack>
        </Box>
      ))}

      <Box>
        <Button onClick={addRow} variant="outlined">
          + Add Schedule
        </Button>
      </Box>
    </Stack>
  );
}

export default CampaignScheduleEditor;
